/**
 * The recovery sheet: the family's keys, on paper.
 *
 * The family code unlocks the family key on any new device, so this sheet IS
 * the way back from losing every device -- which is also why it is treated
 * like a house key, and why the app keeps asking for it to be printed until it
 * has been (see `recoverySaved`). Each child's code is on it too, with a
 * square to scan, because children's codes last now and a sheet on the fridge
 * is the quickest way to put a child's page on a new tablet.
 *
 * Built on this device from what it already holds. It is shown over the app
 * and printed with the browser's own print; printing hides everything else
 * (`body.printing-sheet` in app.css). Nothing is sent anywhere.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatFamilyCode, joinLink, readFamilyCode } from '../family/families';
import { mintChildCode, readChildCode } from '../family/devices';
import { format as formatCode } from '../family/codes';
import type { Person } from '../family/state';
import { QrCode } from '../components/QrCode';
import { childLink } from './ChildCode';
import { publicOrigin } from '../native/platform';

const SAVED = 'valley.recovery.';

/** When this device last printed the sheet or saved a backup, if ever. */
export function recoverySaved(familyId: string): string | null {
  try {
    return localStorage.getItem(SAVED + familyId);
  } catch {
    return null;
  }
}

export function noteRecoverySaved(familyId: string): void {
  try {
    localStorage.setItem(SAVED + familyId, new Date().toISOString().slice(0, 10));
  } catch {
    /* private window: the reminder simply comes back */
  }
}

export function RecoverySheet({
  familyId,
  familyKey,
  familyName,
  people,
  onClose,
}: {
  familyId: string;
  familyKey: CryptoKey;
  familyName: string;
  people: Person[];
  onClose: () => void;
}) {
  const [familyCode, setFamilyCode] = useState<string | null | undefined>(undefined);
  const [codes, setCodes] = useState<Record<string, string | null>>({});

  useEffect(() => {
    let cancelled = false;
    void readFamilyCode(familyId, familyKey)
      .catch(() => null)
      .then((code) => !cancelled && setFamilyCode(code));
    void Promise.all(
      people.map(async (person) => {
        // A child with no code gets one: the sheet is where it is wanted.
        const code = (await readChildCode(person, familyKey)) ?? (await mintChildCode(familyId, familyKey, person.id).catch(() => null));
        return [person.id, code] as const;
      }),
    ).then((pairs) => !cancelled && setCodes(Object.fromEntries(pairs)));
    return () => {
      cancelled = true;
    };
  }, [familyId, familyKey, people]);

  useEffect(() => {
    document.body.classList.add('printing-sheet');
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    return () => {
      document.body.classList.remove('printing-sheet');
      window.removeEventListener('keydown', key);
    };
  }, [onClose]);

  const ready = familyCode !== undefined && Object.keys(codes).length === people.length;
  const origin = publicOrigin();
  const today = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

  return createPortal(
    <div className="recovery-sheet" role="dialog" aria-modal="true" aria-labelledby="recovery-title">
      <div className="recovery-controls">
        <button
          type="button"
          className="primary"
          disabled={!ready}
          onClick={() => {
            noteRecoverySaved(familyId);
            window.print();
          }}
        >
          {ready ? 'Print it' : 'Getting the codes…'}
        </button>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>

      <article className="recovery-page">
        <h1 id="recovery-title">{familyName || 'Our family'} &mdash; Birbly recovery sheet</h1>
        <p className="recovery-date">Printed {today}. Keep it somewhere safe, like a spare house key.</p>

        <section className="recovery-block">
          <div>
            <h2>Family code</h2>
            <p className="recovery-code">{familyCode ? formatFamilyCode(familyCode) : familyCode === null ? 'Not set — make one in Settings' : '…'}</p>
            <p>
              <strong>If you lose every device:</strong> go to <strong>{origin}</strong>, sign in (or
              make an account), choose to join a family, and type this code. Everything comes back.
            </p>
            <p>
              Anyone with this code can ask to join your family, so keep it private. You can change it
              in Settings, and then print a new sheet.
            </p>
          </div>
          {familyCode ? <QrCode text={joinLink(familyCode)} label="QR code to join the family" /> : null}
        </section>

        {people.length ? <h2 className="recovery-kids">Each child&rsquo;s own page</h2> : null}
        <div className="recovery-children">
          {people.map((person) => {
            const code = codes[person.id];
            return (
              <section className="recovery-child" key={person.id}>
                <div>
                  <h3>{person.name || 'Somebody'}</h3>
                  <p className="recovery-code small">{code ? formatCode(code) : '…'}</p>
                  <p>
                    Open <strong>{origin}/me</strong> and type this, or scan the square. A grown-up
                    approves each new device.
                  </p>
                </div>
                {code ? <QrCode text={childLink(code)} label={`QR code for ${person.name || 'their'} page`} /> : null}
              </section>
            );
          })}
        </div>

        <p className="recovery-foot">
          Birbly keeps everything encrypted with your family&rsquo;s key, and never has that key
          itself. That is why nobody &mdash; not even Birbly &mdash; can open your family&rsquo;s data
          without this code.
        </p>
      </article>
    </div>,
    document.body,
  );
}
